import React, {type RefObject} from 'react';

interface ArrowProps {
    /**
     * A string representing the CSS class to be applied to the ArrowIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the ArrowIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the ArrowIcon element.
     */
    width?: number | string;
}

/**
 * The `Arrow` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `ArrowProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.className A string representing the CSS class to be applied to the ArrowIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the ArrowIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the ArrowIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const ArrowComponent = <Arrow className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Arrow = ({
    className = '',
    color1 = '#14181F',
    height = 10,
    ref,
    testId = 'Arrow',
    width = 6
}: ArrowProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 6 10"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path
            d="M4.67 1.5 1.65 5l3.02 3.5"
            fill="transparent"
            stroke={color1}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
        />
    </svg>
);

Arrow.displayName = 'Arrow';